import type { ImageProps } from 'next/image';
import Image from 'next/image';

import type { LogoName } from '@sola/schema/lib/logos.ts';

import { cn } from '../../utils/helpers.ts';

const logoColors = {
  blue: 'blue',
  black: 'black',
  white: 'white',
  full: 'full',
  blueLegacy: 'blueLegacy',
  gray: 'gray',
  darkPink: 'darkPink',
} as const;
type LogoColors = (typeof logoColors)[keyof typeof logoColors];

export type LogoChooserProps = Omit<ImageProps, 'src' | 'alt' | 'color'> & {
  name: LogoName;
  alt?: string;
  color?: LogoColors;
  priority?: boolean;
};

type LogoImgData = {
  base: string;
  defaultColor?: string;
  width: number;
  height: number;
  colors?: LogoColors[];
};

const logoSVG: Record<LogoName, LogoImgData> = {
  compassion: {
    base: 'LogoCompassion',
    defaultColor: logoColors.blue,
    width: 168,
    height: 64,
    colors: [
      logoColors.black,
      logoColors.blue,
      logoColors.white,
      logoColors.gray,
      logoColors.blueLegacy,
    ],
  },
  'compassion-explorer': {
    base: 'LogoCompassionExplorer',
    defaultColor: logoColors.full,
    width: 200,
    height: 64,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.full,
    ],
  },
  'compassion-journey': {
    base: 'LogoCompassionJourney',
    width: 270,
    height: 64,
    colors: [logoColors.black, logoColors.white, logoColors.gray],
  },
  'compassion-moments': {
    base: 'LogoCompassionMoments',
    width: 253,
    height: 64,
    colors: [logoColors.black, logoColors.white, logoColors.gray],
  },
  'compassion-simple': {
    base: 'LogoCompassionSimple',
    defaultColor: logoColors.blue,
    width: 161,
    height: 64,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.blue,
      logoColors.blueLegacy,
    ],
  },
  'compassion-sunday': {
    base: 'LogoCompassionSunday',
    width: 221,
    height: 64,
    colors: [logoColors.black, logoColors.white, logoColors.gray],
  },
  'compassion-experience': {
    base: 'LogoCompassionExperience',
    defaultColor: logoColors.full,
    width: 166,
    height: 64,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.full,
    ],
  },
  skippy: {
    base: 'LogoSkippy',
    width: 64,
    height: 64,
    colors: [
      logoColors.black,
      logoColors.white,
      logoColors.gray,
      logoColors.blue,
      logoColors.blueLegacy,
    ],
  },
  'compassion-accelerators': {
    base: 'LogoCompassionAccelerators',
    width: 64,
    height: 64,
  },
  'compassion-accelerators-text': {
    base: 'LogoCompassionAcceleratorsText',
    width: 277,
    height: 64,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-advocacy-partners': {
    base: 'LogoCompassionAdvocacyPartners',
    defaultColor: logoColors.full,
    width: 145,
    height: 64,
    colors: [logoColors.white, logoColors.full],
  },
  'compassion-called-to-care': {
    base: 'LogoCompassionCalledToCare',
    defaultColor: logoColors.full,
    width: 90,
    height: 90,
    colors: [logoColors.white, logoColors.full],
  },
  'compassion-giving-tuesday': {
    base: 'LogoCompassionGivingTuesday',
    defaultColor: logoColors.full,
    width: 559,
    height: 64,
    colors: [logoColors.white, logoColors.full],
  },
  'compassion-multipliers': {
    base: 'LogoCompassionMultipliers',
    width: 64,
    height: 64,
  },
  'compassion-multipliers-text': {
    base: 'LogoCompassionMultipliersText',
    width: 250,
    height: 64,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-nurturers': {
    base: 'LogoCompassionNurturers',
    width: 64,
    height: 64,
  },
  'compassion-nurturers-wordmark-solo-black': {
    base: 'LogoCompassionNurturersWordmarkSoloBlack',
    width: 318,
    height: 53,
    colors: [logoColors.white],
  },
  'compassion-nurturers-wordmark-solo-midpink': {
    base: 'LogoCompassionNurturersWordmarkSoloMidpink',
    width: 318,
    height: 53,
  },
  'compassion-nurturers-wordmark-solo-lightpink': {
    base: 'LogoCompassionNurturersWordmarkSoloLightpink',
    width: 318,
    height: 53,
  },
  'compassion-nurturers-wordmark-solo-darkpink': {
    base: 'LogoCompassionNurturersWordmarkSoloDarkpink',
    width: 318,
    height: 53,
  },
  'compassion-nurturers-wordmark-center-black': {
    base: 'LogoCompassionNurturersWordmarkCenterBlack',
    width: 224,
    height: 72,
    colors: [logoColors.white],
  },
  'compassion-nurturers-wordmark-center-midpink': {
    base: 'LogoCompassionNurturersWordmarkCenterMidpink',
    width: 224,
    height: 72,
  },
  'compassion-nurturers-wordmark-center-lightpink': {
    base: 'LogoCompassionNurturersWordmarkCenterLightpink',
    width: 224,
    height: 72,
  },
  'compassion-nurturers-wordmark-center-darkpink': {
    base: 'LogoCompassionNurturersWordmarkCenterDarkpink',
    width: 224,
    height: 72,
  },
  'compassion-nurturers-wordmark-left-black': {
    base: 'LogoCompassionNurturersWordmarkLeftBlack',
    width: 305,
    height: 97,
    colors: [logoColors.white],
  },
  'compassion-nurturers-wordmark-left-midpink': {
    base: 'LogoCompassionNurturersWordmarkLeftMidpink',
    width: 305,
    height: 97,
  },
  'compassion-nurturers-wordmark-left-lightpink': {
    base: 'LogoCompassionNurturersWordmarkLeftLightpink',
    width: 305,
    height: 97,
  },
  'compassion-nurturers-wordmark-left-darkpink': {
    base: 'LogoCompassionNurturersWordmarkLeftDarkpink',
    width: 305,
    height: 97,
  },
  'compassion-nurturers-wordmark-tag-darkpink': {
    base: 'LogoCompassionNurturersWordmarkTagDarkpink',
    width: 288,
    height: 85,
  },
  'compassion-nurturers-wordmark-tag-lightpink': {
    base: 'LogoCompassionNurturersWordmarkTagLightpink',
    width: 288,
    height: 85,
  },
  'compassion-nurturers-text': {
    base: 'LogoCompassionNurturersText',
    width: 237,
    height: 64,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-responders': {
    base: 'LogoCompassionResponders',
    width: 64,
    height: 64,
  },
  'compassion-responders-text': {
    base: 'LogoCompassionRespondersText',
    width: 260,
    height: 64,
    colors: [logoColors.black, logoColors.white],
  },
  'compassion-unto-jesus': {
    base: 'LogoCompassionUntoJesus',
    defaultColor: logoColors.full,
    width: 420,
    height: 64,
    colors: [logoColors.white, logoColors.full],
  },
  'apple-pay': {
    base: 'LogoApplePay',
    width: 153,
    height: 64,
    colors: [logoColors.black, logoColors.white],
  },
  'k-m-circle': {
    base: 'LogoKMCircle',
    defaultColor: logoColors.full,
    width: 90,
    height: 90,
    colors: [logoColors.full],
  },
  'practicing-the-way': {
    base: 'LogoPracticingTheWay',
    defaultColor: logoColors.full,
    width: 130,
    height: 64,
    colors: [logoColors.white, logoColors.full],
  },
  'trust-charity-watch': {
    base: 'LogoTrustCharityWatch',
    width: 85,
    height: 64,
  },
  'trust-ecfa': { base: 'LogoTrustECFA', width: 64, height: 64 },
  'trust-bbb': { base: 'LogoTrustBBB', width: 43, height: 64 },
  'trust-guide-star': { base: 'LogoTrustGuideStar', width: 66, height: 64 },
  'trust-charity-navigator': {
    base: 'LogoTrustCharityNavigator',
    width: 200,
    height: 64,
  },
  'trust-norton': { base: 'LogoTrustNorton', width: 116, height: 64 },
  'trust-seal-of-transparency-guide-star': {
    base: 'LogoTrustSealOfTransparencyGuideStar',
    width: 64,
    height: 64,
  },
};

const getLogoData = (name: LogoName, color?: LogoColors) => {
  const logoData = logoSVG[name];

  const hasSelectedColor = color && logoData.colors?.includes(color);

  let { base } = logoData;

  if (hasSelectedColor) {
    base = `${base}--${color}`;
  } else if (logoData.defaultColor) {
    base = `${base}--${logoData.defaultColor}`;
  }

  return {
    base,
    width: logoData.width,
    height: logoData.height,
  };
};

export const LogoChooser = ({
  name,
  alt,
  width,
  height,
  color,
  priority = false,
  ...props
}: LogoChooserProps) => {
  const logoData = getLogoData(name, color);

  let logoWidth: number;
  let logoHeight: number;

  // Calculate correct dimensions based on what's provided
  if (width && !height) {
    logoWidth = width as number;
    logoHeight = (logoData.height / logoData.width) * logoWidth;
  } else if (height && !width) {
    logoHeight = height as number;
    logoWidth = (logoData.width / logoData.height) * logoHeight;
  } else {
    // Both specified: use both
    logoWidth = (width as number) ?? logoData.width;
    logoHeight = (height as number) ?? logoData.height;
  }

  return (
    <Image
      {...props}
      className={cn('LogoChooser LogoChooser__svg', props.className)}
      src={`/logos/${logoData.base}.svg`}
      alt={alt ?? name}
      width={logoWidth}
      height={logoHeight}
      priority={priority}
    />
  );
};
